import React from 'react';
import { TargetIcon, EyeIcon, HeartIcon, ZapIcon } from 'lucide-react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
export function AboutPage() {
  usePageSEO({
    canonical: '/about',
    keywords: 'about Design Flow Digitals, Shopify development company, ecommerce agency, Shopify experts',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'About',
      path: '/about'
    }]),
    title: 'About | Design Flow Digitals',
    description: 'Learn about Design Flow Digitals, a Shopify-focused development company building premium ecommerce experiences with custom themes, apps, and conversion-optimized stores.'
  });
  const philosophies = [{
    title: 'Simple',
    icon: <HeartIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-466" data-mp="1gkgsz2pe9rpf" />
  }, {
    title: 'Fast',
    icon: <ZapIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-467" data-mp="1bn76nvxaxw5t" />
  }, {
    title: 'Beautiful',
    icon: <EyeIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-468" data-mp="2evfxj9ykb3us" />
  }, {
    title: 'Effective',
    icon: <TargetIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-469" data-mp="2jw3jvibuiwio" />
  }];
  return <div className="pt-32 pb-20" data-id="element-470" data-mp="25way81f6f6hk">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-471" data-mp="25xm4c7w3gqbu">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-472" data-mp="2av5p7qnas1cr">
          About Design Flow Digitals
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-473" data-mp="2yl0042o23fud">
          Design Flow Digitals is a Shopify development company focused on
          building premium ecommerce experiences.
        </p>
      </section>

      {/* Specializations & Goal */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-474" data-mp="2tsgdyhbazcdy">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-475" data-mp="3hij3oqrqw69e">
          <div data-id="element-476" data-mp="3m9spthe34rgj">
            <h2 className="text-3xl font-bold mb-8" data-id="element-477" data-mp="3maavt5qjuk1n">We specialize in:</h2>
            <ul className="space-y-4" data-id="element-478" data-mp="3mbm159zn7zuv">
              {['Shopify theme development', 'Shopify app development', 'Custom Shopify store design', 'Performance optimization'].map((item, i) => <li key={i} className="flex items-center gap-4 text-lg text-gray-300" data-id="element-479" data-mp="3m60qm7n36stq" data-mpi={i}>
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-480" data-mp="3d6ncv2rxji37" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="glass-card p-10 border-[#D4AF37]/20 relative overflow-hidden" data-id="element-481" data-mp="38evx7nabawru">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" data-id="element-482" data-mp="33hlvp99neunn" />
            <h2 className="text-2xl font-bold mb-4 text-white" data-id="element-483" data-mp="2yjkd0pg4wxcp">
              Our goal is simple:
            </h2>
            <p className="text-2xl text-gradient-gold font-medium leading-relaxed" data-id="element-484" data-mp="2tl0qamtpvhja">
              Create ecommerce products and experiences that help businesses
              grow faster.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding" data-id="element-485" data-mp="18zknhuid3ht9">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-486" data-mp="1dzyst4skwdnl">
          <h2 className="text-4xl font-bold mb-6" data-id="element-487" data-mp="1e0i442nq43sn">Our Philosophy</h2>
          <p className="text-xl text-gray-400" data-id="element-488" data-mp="1izbcxhs2f6mh">
            Great ecommerce design should be:
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-id="element-489" data-mp="lcvykouxqp35">
          {philosophies.map((phil, i) => <div key={i} className="glass-card p-8 text-center flex flex-col items-center gap-4" data-id="element-490" data-mp="qans1nb8aj9g" data-mpi={i}>
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-491" data-mp="zzxkj49zi3y3">
                {phil.icon}
              </div>
              <h3 className="text-xl font-bold text-white" data-id="element-492" data-mp="2ka4oa3l98l7">{phil.title}</h3>
            </div>)}
        </div>
        <div className="max-w-4xl mx-auto text-center" data-id="element-493" data-mp="7htixa0adld5">
          <p className="text-xl text-gray-300 leading-relaxed" data-id="element-494" data-mp="29a3ppiyicle3">
            Inspired by the product design principles used by companies like
            Apple, we believe that simplicity and performance create the best
            user experiences.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-black" data-id="element-495" data-mp="2e6lpe4ezdtjk">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-496" data-mp="2swjkz5f8plis">
          <div className="glass-card p-12" data-id="element-497" data-mp="2sz5pew6u3kws">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-498" data-mp="2nyrgzd1bm8y8">
              Our Mission
            </h2>
            <p className="text-2xl text-white font-medium leading-relaxed" data-id="element-499" data-mp="31wy8mbfofcpg">
              To help modern ecommerce brands launch and scale with powerful
              Shopify technology.
            </p>
          </div>
          <div className="glass-card p-12" data-id="element-500" data-mp="17ljivumo1d9p">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-501" data-mp="12lf203huigiy">
              Our Vision
            </h2>
            <p className="text-2xl text-white font-medium leading-relaxed" data-id="element-502" data-mp="3e8qv70iyk4is">
              To become one of the most trusted creators of Shopify themes,
              apps, and ecommerce experiences globally.
            </p>
          </div>
        </div>
      </section>

      {/* About the Founder */}
      <section className="section-padding border-t border-white/5" data-id="element-503" data-mp="39ffnyya8hlqc">
        <div className="max-w-5xl mx-auto" data-id="element-504" data-mp="2vgfw7hrfcqpy">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-id="element-505" data-mp="2vju1axrh3e13">
            <div className="md:col-span-5" data-id="element-506" data-mp="30hvj7fd5x7az">
              <div className="relative" data-id="element-507" data-mp="2mh2p2vqrwe8e">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#D4AF37]/20 to-transparent rounded-2xl blur-xl" data-id="element-508" data-mp="2midoykh3l1u8" />
                <img src="https://cdn.magicpatterns.com/uploads/mGfabHy4m1VsnCVj3nADJ1/Pankaj_Template_Profile_(1).png" alt="Pankaj Maurya - Founder of Design Flow Digitals" className="relative w-full aspect-[4/5] object-cover rounded-2xl border border-white/10 shadow-2xl" data-id="element-509" data-mp="2qkh5hnimiwmd" />
              </div>
            </div>
            <div className="md:col-span-7" data-id="element-510" data-mp="1snbvuv10so9n">
              <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-511" data-mp="1on08hah9w9uh">
                About the Founder
              </h2>
              <h3 className="text-4xl font-bold mb-6 text-white" data-id="element-512" data-mp="1jprr3c3y89ua">
                Pankaj Maurya
              </h3>
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed" data-id="element-513" data-mp="2ggk7kauso3wl">
                <p data-id="element-514" data-mp="2cde57easywve">
                  As a full-stack Shopify developer with a Master's in Commerce,
                  I founded Design Flow Digitals to bridge the gap between
                  stunning design and high-performance ecommerce architecture.
                </p>
                <p data-id="element-515" data-mp="302p04f5h1x1m">
                  Over my career, I have partnered with over{' '}
                  <strong className="text-white" data-id="element-516" data-mp="2vb6uvewf1aau">1,000+ clients</strong>{' '}
                  globally, delivering custom storefronts that drive measurable
                  revenue growth. My technical expertise spans the entire
                  Shopify ecosystem—I have developed over{' '}
                  <strong className="text-white" data-id="element-517" data-mp="3ckj8k4hhu9dj">
                    100 custom Shopify apps
                  </strong>{' '}
                  for private clients, engineered multiple premium themes, and
                  currently maintain{' '}
                  <strong className="text-white" data-id="element-518" data-mp="3ll7k8sjezxn9">
                    two live public Shopify apps
                  </strong>{' '}
                  (SEO Suite and AI Photoshoot) used by merchants worldwide.
                </p>
                <p data-id="element-519" data-mp="3vdwlyvp5xx5e">
                  Beyond development, I am passionate about elevating the
                  ecommerce community. I actively mentor aspiring developers and
                  store owners on Shopify best practices, conversion
                  optimization, and technical architecture through my dedicated
                  community on{' '}
                  <a href="https://www.skool.com/shopify-skool-6194" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline font-medium" data-id="element-520" data-mp="yfkwfeku2731">
                    Skool
                  </a>
                  .
                </p>
                <p data-id="element-521" data-mp="fq4qucuajofr">
                  My commitment to engineering excellence and merchant success
                  earned me a place in the{' '}
                  <a href="https://www.fiverr.com/webpankaj" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline font-semibold" data-id="element-522" data-mp="asusg7oxc2fs">
                    Fiverr Pro
                  </a>{' '}
                  network, where Fiverr vets professionals through a selective
                  review process. I am also recognized by leading industry
                  platforms including Omnisend and EComposer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notable Clients */}
      <section className="section-padding bg-black border-t border-white/5" data-id="element-523" data-mp="1tqhkmrdbtqe0">
        <div className="max-w-5xl mx-auto" data-id="element-524" data-mp="1yp2d24mc75rw">
          <div className="text-center mb-16" data-id="element-525" data-mp="1ypu91gjuj5ip">
            <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-526" data-mp="1ymq065vfrllp">
              Trusted By
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white" data-id="element-527" data-mp="2rdzw49ydh6lp">
              Notable & Celebrity Clients
            </h3>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-528" data-mp="2ddp1qwxut9vs">
              We engineer high-performance Shopify experiences for industry
              leaders, celebrities, and massive creator brands.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-529" data-mp="16056gyrhmxez">
            <div className="glass-card p-8 border-white/10" data-id="element-530" data-mp="161pyagmf4295">
              <h4 className="text-xl font-bold text-white mb-2" data-id="element-531" data-mp="1qolrqa4mv4ow">Cardi B</h4>
              <p className="text-gray-400 text-sm mb-4" data-id="element-532" data-mp="1vmdszur6435r">
                Developed the official merchandise store (store.cardib.com) for
                global superstar Cardi B. This relationship was established
                through our successful work with another premium client,
                Jeneffer Nemeth of Rich Bitch Clinic.
              </p>
              <a href="https://store.cardib.com/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm font-medium hover:underline" data-id="element-533" data-mp="17vrqeizxnb35">
                View Store →
              </a>
            </div>
            <div className="glass-card p-8 border-white/10" data-id="element-534" data-mp="12y9kz2l4jdzw">
              <h4 className="text-xl font-bold text-white mb-2" data-id="element-535" data-mp="2tbgy90qpeya9">
                Billy Carson
              </h4>
              <p className="text-gray-400 text-sm mb-4" data-id="element-536" data-mp="2xapss7tkj06v">
                Built 4biddenknowledge.shop for the renowned author, researcher,
                and media personality, creating a seamless platform for his
                extensive catalog of literature and apparel.
              </p>
              <a href="https://4biddenknowledge.shop/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm font-medium hover:underline" data-id="element-537" data-mp="vfig0t9foztu">
                View Store →
              </a>
            </div>
            <div className="glass-card p-8 border-white/10" data-id="element-538" data-mp="10cs6mc8k7hj9">
              <h4 className="text-xl font-bold text-white mb-2" data-id="element-539" data-mp="1ed2wpd21os3d">Vector</h4>
              <p className="text-gray-400 text-sm mb-4" data-id="element-540" data-mp="1eaywp679od8d">
                Engineered ohyeah.store for the massive YouTube creator Vector,
                delivering a high-energy storefront capable of handling rapid
                merchandise drops and heavy traffic spikes.
              </p>
              <a href="https://ohyeah.store/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm font-medium hover:underline" data-id="element-541" data-mp="3b7po9mjleihp">
                View Store →
              </a>
            </div>
            <div className="glass-card p-8 border-white/10" data-id="element-542" data-mp="1p2qvmfmzss8b">
              <h4 className="text-xl font-bold text-white mb-2" data-id="element-543" data-mp="1t3aq1bg6lhqr">Daniel Ives</h4>
              <p className="text-gray-400 text-sm mb-4" data-id="element-544" data-mp="1y1uh1q9mtjpz">
                Developed the Snowmilk website for Wall Street famous analyst
                Daniel Ives, translating a unique brand vision into a premium
                digital storefront.
              </p>
              <a href="https://realsnowmilk.com/" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm font-medium hover:underline" data-id="element-545" data-mp="27ti299fli4j0">
                View Store →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Recognition */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-546" data-mp="a4v20lucyop6">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-547" data-mp="53p6kg9bcnby">
          <h2 className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4" data-id="element-548" data-mp="12loro7pgh83">
            Industry Recognition
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-549" data-mp="3sahptphstt4o">
            Featured By Leading Platforms
          </h3>
          <p className="text-lg text-gray-400" data-id="element-550" data-mp="st9tfa8l84qi">
            Pankaj Maurya recognized as a top Shopify expert by industry leaders
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-551" data-mp="k0xzvfi6g16b">
          <a href="https://www.omnisend.com/blog/shopify-experts/" target="_blank" rel="noopener noreferrer" className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group" data-id="element-552" data-mp="f3g0afq1uyaz">
            <div className="flex items-start gap-4" data-id="element-553" data-mp="rbt0ua4j00n2">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shrink-0" data-id="element-554" data-mp="ra9hubrhjbhl">
                <span className="text-2xl" data-id="element-555" data-mp="w8je6poy4i4s">🏆</span>
              </div>
              <div data-id="element-556" data-mp="icpemxa2b54c">
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-557" data-mp="id7fy8bl09j4">
                  Featured by Omnisend
                </h4>
                <p className="text-gray-400 text-sm mb-3" data-id="element-558" data-mp="1b0bt50ktvu5z">
                  Recognized among top Shopify experts for exceptional ecommerce
                  development and design expertise
                </p>
                <span className="text-[#D4AF37] text-sm font-medium inline-flex items-center gap-1" data-id="element-559" data-mp="119ql6mj9ldae">
                  Read Article →
                </span>
              </div>
            </div>
          </a>
          <a href="https://ecomposer.io/blogs/ecommerce/best-shopify-web-designers" target="_blank" rel="noopener noreferrer" className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group" data-id="element-560" data-mp="1twl2xbop5yk6">
            <div className="flex items-start gap-4" data-id="element-561" data-mp="242cklzc2adcg">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shrink-0" data-id="element-562" data-mp="2huj0dkbayibd">
                <span className="text-2xl" data-id="element-563" data-mp="2cw7q58h3i5sm">⭐</span>
              </div>
              <div data-id="element-564" data-mp="1p4sntfpbjohd">
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-565" data-mp="1p63o35jk2e3q">
                  Featured by EComposer
                </h4>
                <p className="text-gray-400 text-sm mb-3" data-id="element-566" data-mp="1z41cpu5bucwv">
                  Listed among the best Shopify web designers for creating
                  high-converting, beautifully designed stores
                </p>
                <span className="text-[#D4AF37] text-sm font-medium inline-flex items-center gap-1" data-id="element-567" data-mp="3ahlxq948sn01">
                  Read Article →
                </span>
              </div>
            </div>
          </a>
          <a href="https://stackinfluence.com/blog/top-10-shopify-experts" target="_blank" rel="noopener noreferrer" className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group" data-id="element-568" data-mp="3dj8cgx4o56nf">
            <div className="flex items-start gap-4" data-id="element-569" data-mp="3rkay58rr4ext">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shrink-0" data-id="element-570" data-mp="3rhyg6vlzu4kr">
                <span className="text-2xl" data-id="element-571" data-mp="2yv3yg6gk9jaz">🚀</span>
              </div>
              <div data-id="element-572" data-mp="2ytaoah6qogy8">
                <h4 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-573" data-mp="33uqd8kc1f7lw">
                  Featured by Stack Influence
                </h4>
                <p className="text-gray-400 text-sm mb-3" data-id="element-574" data-mp="38mzznqdy1pjf">
                  Named among the Top 10 Shopify Experts for delivering
                  high-impact ecommerce development and store growth
                </p>
                <span className="text-[#D4AF37] text-sm font-medium inline-flex items-center gap-1" data-id="element-575" data-mp="28l39uv1dyzzm">
                  Read Article →
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Company Details */}
      <section className="section-padding text-center" data-id="element-576" data-mp="1ylcgiwokar14">
        <div className="inline-block glass-card p-10 border-white/10" data-id="element-577" data-mp="1tovp9tm2fwjt">
          <h2 className="text-2xl font-bold mb-6" data-id="element-578" data-mp="1pminthecqksu">Company Details</h2>
          <div className="text-gray-400 space-y-2" data-id="element-579" data-mp="1poll8mhu3g7l">
            <p className="font-medium text-white" data-id="element-580" data-mp="1kvine260xkll">Design Flow Digitals</p>
            <p data-id="element-581" data-mp="1fwpcmhi4g7p4">Part of Pankaj Maurya LLC</p>
            <p data-id="element-582" data-mp="1fxh87sbpy5fe">67 Wigley Loop</p>
            <p data-id="element-583" data-mp="1ful57pl0kqk3">Whitwell, TN, 37397</p>
            <p data-id="element-584" data-mp="1ay4j10xdkm9n">United States</p>
            <p className="mt-4 pt-4 border-t border-white/10" data-id="element-585" data-mp="1az5wtd5rzmv2">
              Support:{' '}
              <a href="mailto:support@designflowdigitals.com" className="text-[#D4AF37] hover:underline" data-id="element-586" data-mp="15x86y02vdtt5">
                support@designflowdigitals.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>;
}